import Nullable from "@data-force/app/interfaces/nullable";
import React, {RefObject} from "react";
import {connect} from "react-redux";
import {Form} from "antd";
import {
    EditorType,
    ElementsValuesType,
    PresentationElementType,
    SchemaElementsFactory,
    UIElementType,
    ValidationLevel,
} from "@data-force/app";
import "./SingleTask.scss";
import "../../DictationForm/DictationForm.scss";
import {getTranslate, Translate, TranslateFunction} from "react-localize-redux";
import StoreState from "../../../interfaces/store/storeState";
import FilePicker from "../../FilePicker/FilePicker";
import TextElement from "../../DynamicForm/TextElement/TextElement";
import TextAreaElement from "../../DynamicForm/TextAreaElement/TextAreaElement";
import RadioElement from "../../DynamicForm/RadioElement/RadioElement";
import CheckboxElement from "../../DynamicForm/CheckboxElement/CheckboxElement";
import ComboElement from "../../DynamicForm/ComboElement/ComboElement";
import MultiselectElement from "../../DynamicForm/MultiSelectElement/MultiselectElement";
import LiteralElement from "../../DynamicForm/LiteralElement/LiteralElement";
import Container from "../../DynamicForm/Container/Container";
import RepeatableActions from "../../DynamicForm/RepeatableActions/RepeatableActions";
import HistoryParams from "../../../interfaces/historyParams";
import {getHistoryParams, isMetadataValid, isPhaseExpired} from "../../../utils/common";
import CompositeElement from "../../DictationForm/CompositeElement/CompositeElement";
import NumberElement from "../../DynamicForm/NumberElement/NumberElement";
import {Task} from "../../../interfaces/store/actions/payload/task";
import {SaveActiveTaskAction} from "../../../interfaces/store/actions/task";
import {saveActiveTask} from "../../../store/actions/task";
import {JobState} from "../../../interfaces/store/reducers/jobState";

interface StateProps {
    editor: Nullable<EditorType>
    schemaKey: string
    jobId: string
    validMetadata: boolean
    activeTask: Nullable<Task>
    isReadOnly: boolean
}

interface OwnProps {
    headerReference: RefObject<HTMLDivElement>
}

interface DispatchProps {
    saveActiveTask(payload: Task): SaveActiveTaskAction
}

type Props = StateProps & OwnProps & DispatchProps;

class SingleTask extends React.Component<Props> {
    private readonly translatePath: string = "components.SingleTask.SingleTask";

    constructor(props: Props) {
        super(props);

        this.onSave = this.onSave.bind(this);
    }

    onSave() {
        const {activeTask}: Props = this.props;
        if (activeTask) {
            this.props.saveActiveTask(activeTask);
        }
    }

    render() {
        const {
            editor, schemaKey, jobId, validMetadata, headerReference, isReadOnly,
        }: Props = this.props;

        return (
            <div className="df-single-task">
                <div
                    className="df-single-task-header"
                    ref={headerReference}
                    tabIndex={-1}
                >
                    <Translate id={`${this.translatePath}.dictationStudy.text`}/>
                </div>
                <div className="df-single-task-data">
                    {editor && !!editor.batchFieldReferences?.length && (
                        <div className="df-task-metadata">
                            <div className="df-metadata-form">
                                <Form
                                    layout="vertical"
                                    className="df-dictation-form"
                                >
                                    <SchemaElementsFactory
                                        element={editor}
                                        references={editor.batchFieldReferences!}
                                        schemaKey={schemaKey}
                                        initFallbackValues
                                        validationLevel={ValidationLevel.FORM}
                                        metadataId={jobId}
                                        elementsValuesType={ElementsValuesType.JOB}
                                        uiElements={{
                                            [UIElementType.TEXT]: TextElement,
                                            [UIElementType.TEXTAREA]: TextAreaElement,
                                            [UIElementType.RADIO]: RadioElement,
                                            [UIElementType.CHECKBOX]: CheckboxElement,
                                            [UIElementType.COMBO]: ComboElement,
                                            [UIElementType.MULTISELECT]: MultiselectElement,
                                            [UIElementType.LITERAL]: LiteralElement,
                                            [UIElementType.COMPOSITE]: CompositeElement,
                                            [UIElementType.INT]: NumberElement,
                                        }}
                                        onSave={this.onSave}
                                        presentationElements={{
                                            [PresentationElementType.CONTAINER]: Container,
                                            [PresentationElementType.REPEATABLE_ACTIONS]: RepeatableActions,
                                        }}
                                        metadataReadOnly={isReadOnly}
                                    />
                                </Form>
                            </div>
                        </div>
                    )}
                    {validMetadata && <FilePicker/>}
                </div>
            </div>
        );
    }
}

function mapStateToProps({
    schema: {
        elementsValuesMap,
        parsedSchemas,
    },
    project,
    localize,
    job,
}: StoreState): StateProps {
    const historyParams: Nullable<HistoryParams> = getHistoryParams();
    const schemaKey: string = `${project.projectDetails?.schemaId}_${historyParams?.phase}`;
    const editor: EditorType = parsedSchemas[schemaKey] as EditorType;
    const translate: TranslateFunction = getTranslate(localize);
    const jobId: string = historyParams?.jobId || "";
    const {activeTask}: JobState = job;

    return {
        editor: parsedSchemas[schemaKey] as EditorType,
        schemaKey,
        jobId,
        validMetadata: isMetadataValid({
            elementsValuesMap,
            schemaKey,
            editor,
            translate,
            jobId,
            tasks: [],
        }),
        activeTask,
        isReadOnly: isPhaseExpired(job.jobPhase.accessStatus),
    };
}

const mapDispatchToProps: DispatchProps = {
    saveActiveTask,
};

export default connect(mapStateToProps, mapDispatchToProps)(SingleTask);
